import { Star, ExternalLink, PenSquare } from "lucide-react";
import { GOOGLE_LISTING } from "@/data/content";

/**
 * Google Reviews card — links to the live Google Business listing and offers
 * a "Write a review" CTA. Used on Home, Contact, Testimonials and Footer.
 *
 * variant="card"  — full brutalist card (Home / Testimonials / Contact)
 * variant="inline"— compact single-line CTA (Footer)
 */
export default function GoogleReviewsCard({ variant = "card", className = "" }) {
  if (variant === "inline") {
    return (
      <div className={`flex flex-wrap items-center gap-3 text-sm ${className}`}>
        <a
          data-testid="footer-google-view"
          href={GOOGLE_LISTING.mapsUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1 underline decoration-[#FFD700] decoration-2 underline-offset-4 hover:bg-[#FFD700] hover:text-black"
        >
          <Star size={14} fill="#FFD700" stroke="#FFD700" /> Find us on Google
        </a>
        <span className="text-white/40">·</span>
        <a
          data-testid="footer-google-review"
          href={GOOGLE_LISTING.writeReviewUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1 underline decoration-[#FFD700] decoration-2 underline-offset-4 hover:bg-[#FFD700] hover:text-black"
        >
          <PenSquare size={14} /> Write a review
        </a>
      </div>
    );
  }

  return (
    <div data-testid="google-reviews-card" className={`card-brutal ${className}`}>
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center border-2 border-black bg-white">
          <svg viewBox="0 0 48 48" width="22" height="22" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.3l6.8-6.8C35.7 2 30.1 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.4 17.7 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.5 24.5c0-1.7-.2-3.4-.5-5H24v9.5h12.7c-.6 3-2.3 5.5-4.9 7.1l7.6 5.9c4.4-4.1 7.1-10.1 7.1-17.5z"/>
            <path fill="#FBBC05" d="M10.5 28.7c-.5-1.5-.8-3.1-.8-4.7s.3-3.2.8-4.7l-7.9-6.1C1 16.7 0 20.3 0 24s1 7.3 2.6 10.8l7.9-6.1z"/>
            <path fill="#34A853" d="M24 48c6.1 0 11.3-2 15-5.5l-7.6-5.9c-2.1 1.4-4.8 2.2-7.4 2.2-6.3 0-11.6-3.9-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} size={16} fill="#FFD700" stroke="#000" strokeWidth={1.5} />
            ))}
          </div>
          <div className="mt-1 text-xs font-bold uppercase tracking-widest text-black/70">Orina Enterprise on Google</div>
          <p className="mt-2 text-sm text-black/75">
            Verified Google Business listing — Mumbai. Read what our clients say, or share your experience.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              data-testid="google-view-listing"
              href={GOOGLE_LISTING.mapsUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 border-2 border-black bg-white px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#FFD700]"
            >
              View listing <ExternalLink size={12} />
            </a>
            <a
              data-testid="google-write-review"
              href={GOOGLE_LISTING.writeReviewUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 border-2 border-black bg-[#FFD700] px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-[#FFD700]"
            >
              <PenSquare size={12} /> Write a review
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
